import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT, UPLOAD_TIMEOUT, RETRY_CONFIG, axiosConfig, uploadConfig } from '../config/api';
import { attachmentFilename, failureExcelName } from '../pages/campaigning-tool/shell/data/download.js';
import { queryCache, isWrite } from './queryCache';

// =============================================================================
// AXIOS INSTANCE SETUP
// =============================================================================

// Main API client with httpOnly cookie support
const apiClient = axios.create(axiosConfig);

// Upload client with longer timeout
const uploadClient = axios.create(uploadConfig);

// Public API client for endpoints that don't require authentication (no credentials)
const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: false, // Don't send cookies for public endpoints
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// =============================================================================
// REQUEST INTERCEPTORS
// =============================================================================

// Add request ID for correlation
const addRequestId = (config) => {
  config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return config;
};

apiClient.interceptors.request.use(addRequestId, (error) => Promise.reject(error));
uploadClient.interceptors.request.use(addRequestId, (error) => Promise.reject(error));
publicApiClient.interceptors.request.use(addRequestId, (error) => Promise.reject(error));

// =============================================================================
// RESPONSE INTERCEPTORS (Token Refresh + Retry Logic)
// =============================================================================

// Endpoints that should NOT trigger token refresh or redirect on 401
// These are auth-check endpoints where 401 is an expected "not authenticated" state
const skipRefreshEndpoints = ['/auth/me', '/auth/refresh', '/auth/login'];

const handleResponseError = async (error) => {
  const originalRequest = error.config;
  const requestUrl = originalRequest?.url || '';

  // Check if this is an auth-check request that should not trigger refresh/redirect
  const isAuthCheckRequest = skipRefreshEndpoints.some(ep => requestUrl.includes(ep));

  // Handle 401 - Unauthorized (token expired)
  if (error.response?.status === 401 && !originalRequest._retry) {
    // For auth-check endpoints, just reject - don't try refresh or redirect
    // This lets AuthContext handle the unauthenticated state gracefully
    if (isAuthCheckRequest) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Attempt to refresh the token (cookies are sent automatically)
      await apiClient.post('/auth/refresh');
      processQueue(null);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed - redirect to login using React-friendly navigation
      // Check if we're not already on the login page to avoid redirect loops
      if (!window.location.pathname.includes('login')) {
        window.location.href = '/company-user-login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  // Handle 429 - Too Many Requests (never retry these)
  if (error.response?.status === 429) {
    return Promise.reject(error);
  }

  // Handle retry for specific status codes
  if (RETRY_CONFIG.retryOn.includes(error.response?.status) && !originalRequest._retryCount) {
    originalRequest._retryCount = 0;
  }

  if (originalRequest._retryCount < RETRY_CONFIG.maxRetries && 
      RETRY_CONFIG.retryOn.includes(error.response?.status)) {
    originalRequest._retryCount++;
    const delay = RETRY_CONFIG.retryDelay * Math.pow(2, originalRequest._retryCount - 1);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    return apiClient(originalRequest);
  }

  return Promise.reject(error);
};

/**
 * A write may change what a campaign screen is holding in the shared cache (services/queryCache):
 * forget all of it, so the next screen opened asks the server again. Reads and previews do not.
 */
const forgetOnWrite = (response) => {
  if (isWrite(response?.config?.method, response?.config?.url)) queryCache.clear();
  return response;
};

apiClient.interceptors.response.use(forgetOnWrite, handleResponseError);

uploadClient.interceptors.response.use(forgetOnWrite, handleResponseError);

// =============================================================================
// ERROR HANDLING UTILITIES
// =============================================================================

/**
 * Extract user-friendly error message from API error
 */
export const getErrorCode = (error) => error?.response?.data?.error?.code ?? null;
export const getErrorDetails = (error) => error?.response?.data?.error?.details ?? null;

export const getErrorMessage = (error) => {
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Extract validation errors from API response
 */
export const getValidationErrors = (error) => {
  if (error.response?.data?.error?.details) {
    return error.response.data.error.details;
  }
  return null;
};

// =============================================================================
// AUTH SERVICE
// =============================================================================

export const authService = {
  /**
   * Login with email and password
   * @returns {Promise<{user: object}>}
   */
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', {
      email: credentials.email,
      password: credentials.password,
    });
    return response.data;
  },

  /**
   * Second step of super-admin login when 2FA is enabled: exchange the pending token + a TOTP
   * code for a real session.
   */
  verifyLogin2fa: async ({ pending_token, code }) => {
    const response = await apiClient.post('/auth/2fa/login-verify', { pending_token, code });
    return response.data;
  },

  /**
   * Refresh access token (uses httpOnly cookie)
   */
  refresh: async () => {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  },

  /**
   * Logout and clear cookies
   */
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors during logout
      console.warn('Logout error:', error);
    }
  },

  /**
   * Get current authenticated user
   * @returns {Promise<{data: object}>}
   */
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Logout from all devices
   */
  logoutAll: async () => {
    const response = await apiClient.post('/auth/logout-all');
    return response.data;
  },
};

// =============================================================================
// PLATFORM ADMIN — first-login setup + self-service 2FA
// =============================================================================

/** First-login bootstrap: reset the temp password using a setup token (not session-guarded). */
export const platformSetupService = {
  setPassword: async ({ setup_token, new_password }) => {
    const response = await apiClient.post('/platform-admin-setup/password', { setup_token, new_password });
    return response.data;
  },
};

/** Self-service 2FA for a super-admin under a real session (session-guarded). */
export const platform2faService = {
  // Returns { secret, otpauth_url } — does not enable yet.
  enable: async () => {
    const response = await apiClient.post('/platform-admin/2fa/enable');
    return response.data;
  },
  verifyEnable: async ({ code }) => {
    const response = await apiClient.post('/platform-admin/2fa/enable/verify', { code });
    return response.data;
  },
  disable: async ({ password }) => {
    const response = await apiClient.post('/platform-admin/2fa/disable', { password });
    return response.data;
  },
};

/** API Management — consumers (super-admin only). No delete (suspension only). */
export const platformConsumerService = {
  list: async ({ page = 1, limit = 25 } = {}) =>
    (await apiClient.get('/platform-admin/consumers', { params: { page, limit } })).data,
  get: async (id) => (await apiClient.get(`/platform-admin/consumers/${id}`)).data,
  create: async (body) => (await apiClient.post('/platform-admin/consumers', body)).data,
  update: async (id, body) => (await apiClient.patch(`/platform-admin/consumers/${id}`, body)).data,
};

/** API Management — field registry (read-only). */
export const platformFieldRegistryService = {
  list: async (entity) =>
    (await apiClient.get('/platform-admin/field-registry', { params: { entity } })).data,
};

/** API Management — API definitions (create draft -> activate -> revoke). No edit. */
export const platformDefinitionService = {
  list: async ({ page = 1, limit = 25, consumerId, status } = {}) =>
    (await apiClient.get('/platform-admin/api-definitions', {
      params: { page, limit, ...(consumerId ? { consumerId } : {}), ...(status ? { status } : {}) },
    })).data,
  get: async (id) => (await apiClient.get(`/platform-admin/api-definitions/${id}`)).data,
  create: async (body) => (await apiClient.post('/platform-admin/api-definitions', body)).data,
  activate: async (id) => (await apiClient.post(`/platform-admin/api-definitions/${id}/activate`)).data,
  revoke: async (id) => (await apiClient.post(`/platform-admin/api-definitions/${id}/revoke`)).data,
};

// =============================================================================
// USER SERVICE (Admin Only)
// =============================================================================

export const userService = {
  /**
   * Get all users with pagination
   */
  getUsers: async (params = {}) => {
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  /**
   * Get user by ID
   */
  getUserById: async (id) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  /**
   * Create new user
   */
  createUser: async (userData) => {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  /**
   * Update user
   */
  updateUser: async (id, userData) => {
    const response = await apiClient.put(`/users/${id}`, userData);
    return response.data;
  },

  /**
   * Delete (deactivate) user
   */
  deleteUser: async (id) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },

  /**
   * Admin reset user password
   */
  resetPassword: async (id, newPassword) => {
    const response = await apiClient.post(`/users/${id}/reset-password`, {
      new_password: newPassword,
    });
    return response.data;
  },
};

// =============================================================================
// VENDOR SERVICE
// =============================================================================

export const vendorService = {
  /**
   * Get all vendors with filtering and pagination
   */
  getVendors: async (params = {}) => {
    const response = await apiClient.get('/vendors', { params });
    return {
      data: response.data.data,
      meta: response.data.meta,
      total: response.headers['x-total-count'] || response.data.meta?.total,
    };
  },

  /**
   * Get vendor by ID with full details
   */
  getVendorById: async (id) => {
    const response = await apiClient.get(`/vendors/${id}`);
    return response.data;
  },

  /**
   * Create vendor
   */
  createVendor: async (vendorData) => {
    const response = await apiClient.post('/vendors', vendorData);
    return response.data;
  },

  /**
   * Update vendor (with optimistic locking)
   */
  updateVendor: async (id, vendorData) => {
    const response = await apiClient.put(`/vendors/${id}`, vendorData);
    return response.data;
  },

  /**
   * Soft delete vendor
   */
  deleteVendor: async (id, reason) => {
    const response = await apiClient.delete(`/vendors/${id}`, { 
      data: { reason } 
    });
    return response.data;
  },

  /**
   * Restore soft-deleted vendor
   */
  restoreVendor: async (id) => {
    const response = await apiClient.post(`/vendors/${id}/restore`);
    return response.data;
  },

  /**
   * Assign vendor code
   */
  assignVendorCode: async (id, vendorCode) => {
    const response = await apiClient.post(`/vendors/${id}/assign-code`, { vendor_code: vendorCode });
    return response.data;
  },

  /**
   * Change vendor status
   */
  changeStatus: async (id, status, reason) => {
    // Convert status to uppercase to match backend validation
    const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : status;
    const response = await apiClient.patch(`/vendors/${id}/status`, { status: normalizedStatus, reason });
    return response.data;
  },

  /**
   * Get vendor audit log
   */
  getAuditLog: async (id, limit = 50) => {
    const response = await apiClient.get(`/vendors/${id}/audit-log`, { params: { limit } });
    return response.data;
  },

  /**
   * Get vendor statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/vendors/stats');
    return response.data;
  },

  /**
   * Bulk status update
   */
  bulkStatusUpdate: async (vendorIds, status, reason) => {
    const response = await apiClient.post('/vendors/bulk/status', {
      vendor_ids: vendorIds,
      status,
      reason,
    });
    return response.data;
  },

  /**
   * Request document resubmission from vendor.
   * @param {number} vendorId - Vendor ID
   * @param {object} data - { requested_types: string[], custom_docs: { name: string }[] }
   */
  requestDocuments: async (vendorId, data) => {
    const response = await apiClient.post(`/vendors/${vendorId}/request-documents`, data);
    return response.data;
  },

  /**
   * Get document resubmission requests for a vendor.
   * @param {number} vendorId - Vendor ID
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  getDocumentRequests: async (vendorId) => {
    const response = await apiClient.get(`/vendors/${vendorId}/document-requests`);
    return response.data;
  },

  /**
   * Resend the approval Excel email (same as acknowledgement confirmation).
   * Sends vendor data, documents, agreements, and Excel to admin.
   * @param {number} vendorId - Vendor ID
   * @param {object} options - Optional: questionnaire_data, email_to, cc
   * @returns {Promise<{success: boolean, message: string}>}
   */
  resendApprovalExcelEmail: async (vendorId, options = {}) => {
    const response = await apiClient.post(`/vendors/${vendorId}/send-excel-email`, {
      questionnaire_data: options.questionnaire_data || {},
      email_to: options.email_to,
      cc: options.cc,
      generated_documents: options.generated_documents || [],
    }, { timeout: 90000 });
    return response.data;
  },

  /**
   * Regenerate agreement PDFs (retry when generation failed).
   * @param {number} vendorId - Vendor ID
   * @param {boolean} [withSignature=true] - Include digital signature in PDFs
   */
  regenerateAgreements: async (vendorId, withSignature = true) => {
    const response = await apiClient.post(`/vendors/${vendorId}/regenerate-agreements`, {
      withSignature,
    });
    return response.data;
  },

  regenerateMsmeDeclaration: async (vendorId) => {
    const response = await apiClient.post(`/vendors/${vendorId}/regenerate-msme-declaration`);
    return response.data;
  },

  /**
   * Select agreement types to generate and start generation (for vendors with no signed flags).
   * @param {number} vendorId - Vendor ID
   * @param {Object} options - { agreementTypes: string[], withSignature?: boolean }
   */
  selectAndGenerateAgreements: async (vendorId, { agreementTypes, withSignature = true }) => {
    const response = await apiClient.post(`/vendors/${vendorId}/select-and-generate-agreements`, {
      agreement_types: agreementTypes,
      with_signature: withSignature,
    });
    return response.data;
  },

  /**
   * Create rectification link for rejected vendor
   */
  createRectificationLink: async (vendorId, data) => {
    const response = await apiClient.post(`/vendors/${vendorId}/create-rectification-link`, data);
    return response.data;
  },

  /**
   * Permanently delete a rejected vendor
   */
  permanentDeleteVendor: async (vendorId) => {
    const response = await apiClient.delete(`/vendors/${vendorId}/permanent-delete`);
    return response.data;
  },

  /**
   * Bulk delete
   */
  bulkDelete: async (vendorIds, reason) => {
    const response = await apiClient.post('/vendors/bulk/delete', {
      vendor_ids: vendorIds,
      reason,
    });
    return response.data;
  },

  /**
   * Manually trigger ERP vendor code synchronization
   */
  syncERP: async () => {
    const response = await apiClient.post('/vendors/sync-erp');
    return response.data;
  },

  /**
   * Get the current status of ERP manual sync (running / last result)
   */
  getSyncERPStatus: async () => {
    const response = await apiClient.get('/vendors/sync-erp/status');
    return response.data;
  },

  /**
   * Download a single vendor's Excel export (blob).
   */
  exportVendorExcel: async (id) => {
    const response = await apiClient.get(`/vendors/${id}/export/excel`, { responseType: 'blob' });
    return response.data;
  },
};

// =============================================================================
// DOCUMENT SERVICE
// =============================================================================

export const documentService = {
  /**
   * Get documents for a vendor
   */
  getVendorDocuments: async (vendorId) => {
    const response = await apiClient.get(`/documents/vendor/${vendorId}`);
    return response.data;
  },

  /**
   * Get document by ID
   */
  getDocumentById: async (id) => {
    const response = await apiClient.get(`/documents/${id}`);
    return response.data;
  },

  /**
   * Download document
   */
  downloadDocument: async (id) => {
    const response = await apiClient.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  /**
   * Upload document for vendor
   */
  /** What a stored document said, measured against the vendor record as it stands now. */
  getRead: async (documentId) => (await apiClient.get(`/documents/${documentId}/read`)).data,
  /** Read it again — a re-scan, or a document uploaded before reading existed. */
  readAgain: async (documentId) => (await apiClient.post(`/documents/${documentId}/read`)).data,
  /**
   * Write the accepted values onto the vendor. `confirmed` must be true: the server refuses a
   * read nobody has vouched for, and records the signed-in user as the one who did.
   */
  applyRead: async (documentId, columns) =>
    (await apiClient.post(`/documents/${documentId}/apply`, { columns, confirmed: true })).data,

  uploadDocument: async (vendorId, file, documentType, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    const response = await uploadClient.post(`/documents/vendor/${vendorId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined,
    });
    return response.data;
  },

  /**
   * Update document status
   */
  updateStatus: async (id, status) => {
    const response = await apiClient.patch(`/documents/${id}/status`, { status });
    return response.data;
  },

  /**
   * Delete document
   */
  deleteDocument: async (id) => {
    const response = await apiClient.delete(`/documents/${id}`);
    return response.data;
  },

  /**
   * Get document statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/documents/stats');
    return response.data;
  },

  /**
   * Generate agreement PDF on-demand (backend uses vendorhub-pdf-templates)
   * @param {number} vendorId
   * @param {string} agreementType - NDA, SQA, FOUR_M, CODE_OF_CONDUCT, COMPLIANCE_AGREEMENT, SELF_DECLARATION, OVERSEAS_AGREEMENT
   * @returns {Promise<Blob>}
   */
  getAgreementPdf: async (vendorId, agreementType) => {
    const response = await apiClient.get(`/vendors/${vendorId}/agreements/${agreementType}/generate`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// =============================================================================
// REGISTRATION SERVICE (Staged 7-day Flow)
// =============================================================================

export const registrationService = {
  /**
   * Start a new registration session (public endpoint)
   */
  startSession: async (inviteToken, clientFingerprint) => {
    const response = await publicApiClient.post('/registration/start', {
      invite_token: inviteToken,
      client_fingerprint: clientFingerprint,
    });
    return response.data;
  },
  /**
   * DEMO / TRIAL ONLY — create a real, email-pre-verified session for the Campaigning Tool demo so
   * live SurePass verification runs in registration mode. Backed by the gated /registration/demo
   * endpoint (mock-only); never used by real vendors.
   */
  startDemoSession: async (scenario) => {
    const response = await publicApiClient.post('/registration/demo/start', { scenario });
    return response.data;
  },

  /**
   * Resume existing session (public endpoint)
   */
  resumeSession: async (sessionId) => {
    const response = await publicApiClient.get(`/registration/${sessionId}`);
    return response.data;
  },

  /**
   * Email a one-time verification code to the invite's email (public endpoint).
   * The server decides the address — the client never sends one.
   */
  sendEmailOtp: async (sessionId) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/email-otp/send`);
    return response.data;
  },

  /**
   * Verify the emailed code; unlocks steps 2+ and submit for this session (public endpoint)
   */
  verifyEmailOtp: async (sessionId, code) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/email-otp/verify`, { code });
    return response.data;
  },

  // ----- Surepass KYC (server-side registry verification; the browser never sees the token) -----
  setIdentityCountry: async (sessionId, country) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/country`, { country });
    return response.data;
  },
  verifyGstin: async (sessionId, gstin) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/gstin/verify`, { gstin, consent: true });
    return response.data;
  },
  lookupUdyam: async (sessionId, udyam) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/udyam/lookup`, { udyam });
    return response.data;
  },
  // The registry could not answer (or is slow): keep the detail as typed; Amber verifies it before approval.
  gstinManual: async (sessionId, gstin) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/gstin/manual`, { gstin });
    return response.data;
  },
  udyamManual: async (sessionId, udyam) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/udyam/manual`, { udyam });
    return response.data;
  },
  bankManual: async (sessionId, { account_number, ifsc } = {}) => {
    const body = {};
    if (account_number) body.account_number = account_number;
    if (ifsc) body.ifsc = ifsc;
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/bank/manual`, body);
    return response.data;
  },
  // "Change": undo a lookup so the number can be entered again (what it filled is unlocked and emptied).
  clearGstin: async (sessionId) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/gstin/clear`, {});
    return response.data;
  },
  clearUdyam: async (sessionId) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/udyam/clear`, {});
    return response.data;
  },
  getKycStatus: async (sessionId) => {
    const response = await publicApiClient.get(`/registration/${sessionId}/kyc/status`);
    return response.data;
  },
  verifyBank: async (sessionId, { account_number, ifsc, method = 'typed' }) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/bank/verify`, { account_number, ifsc, method });
    return response.data;
  },
  chequeOcr: async (sessionId, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/bank/cheque-ocr`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  udyamCertOcr: async (sessionId, file, expectedClassificationDate) => {
    const form = new FormData();
    form.append('file', file);
    if (expectedClassificationDate) form.append('expected_classification_date', expectedClassificationDate);
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/udyam/cert-ocr`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  /**
   * Read any uploaded document and see what it would fill in: POST .../kyc/documents/:kind/read.
   * `kind` is the DocumentType (PAN_CARD, GST_CERTIFICATE, BANK_PROOF, MSME_CERTIFICATE,
   * INCORPORATION_CERTIFICATE). The document is kept as the vendor's file of that type either way.
   */
  readDocument: async (sessionId, kind, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/documents/${kind}/read`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  /**
   * Write the fields the vendor accepted. Omit `fields` to accept everything that was offered.
   * `confirmed` must be true — the server refuses to write a read nobody has vouched for — and
   * `confirmedBy` records who did, because a reader can misread a character.
   */
  applyDocumentRead: async (sessionId, kind, fields, { confirmed = false, confirmedBy = '' } = {}) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/documents/${kind}/apply`, {
      ...(fields ? { fields } : {}),
      confirmed,
      confirmed_by: confirmedBy,
    });
    return response.data;
  },
  /** What a document said last time, re-measured against the form as it stands now. */
  lastDocumentRead: async (sessionId, kind) => {
    const response = await publicApiClient.get(`/registration/${sessionId}/kyc/documents/${kind}/read`);
    return response.data;
  },
  documentNumberOcr: async (sessionId, kind, file, expectedNumber) => {
    const form = new FormData();
    form.append('file', file);
    if (expectedNumber) form.append('expected_number', expectedNumber);
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/${kind}/doc-ocr`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  upiInitialize: async (sessionId) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/bank/upi/initialize`, {});
    return response.data;
  },
  upiStatus: async (sessionId) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/bank/upi/status`, {});
    return response.data;
  },
  clearBank: async (sessionId) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/bank/clear`, {});
    return response.data;
  },
  sendUdyamOtp: async (sessionId, mobile_number) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/udyam/otp/send`, { mobile_number });
    return response.data;
  },
  verifyUdyamOtp: async (sessionId, otp) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/kyc/udyam/otp/verify`, { otp });
    return response.data;
  },

  /**
   * Save step data (public endpoint)
   */
  saveStepData: async (sessionId, step, data) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/data`, {
      step,
      data,
    });
    return response.data;
  },

  /**
   * Upload file for registration (public endpoint)
   */
  uploadFile: async (sessionId, file, fileType, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    // Create a public upload client without credentials
    const publicUploadClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: UPLOAD_TIMEOUT,
      withCredentials: false,
      headers: {
        'Accept': 'application/json',
      },
    });

    const response = await publicUploadClient.post(`/registration/${sessionId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined,
    });
    return response.data;
  },

  /**
   * Delete staged file (public endpoint)
   */
  deleteFile: async (sessionId, fileId) => {
    const response = await publicApiClient.delete(`/registration/${sessionId}/files/${fileId}`);
    return response.data;
  },

  /**
   * Preview a staged file (public endpoint)
   */
  previewStagedFile: async (sessionId, fileId) => {
    const response = await publicApiClient.get(
      `/registration/${sessionId}/files/${fileId}/preview`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Submit registration (public endpoint)
   */
  submit: async (sessionId, submissionId, generatedAgreements = []) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/submit`, {
      submission_id: submissionId,
      generated_agreements: generatedAgreements,
    });
    return response.data;
  },
};

// =============================================================================
// ACKNOWLEDGEMENT SERVICE
// =============================================================================

export const acknowledgementService = {
  /**
   * Validate acknowledgement token (public endpoint)
   */
  validateToken: async (token) => {
    const response = await publicApiClient.get(`/acknowledgements/validate/${token}`);
    return response.data;
  },

  /**
   * Vendor approves the questionnaire (public endpoint)
   */
  approve: async (token) => {
    const response = await publicApiClient.post(`/acknowledgements/${token}/approve`);
    return response.data;
  },

  /**
   * Vendor conflicts with questionnaire and proposes changes (public endpoint)
   */
  conflict: async (token, data) => {
    const response = await publicApiClient.post(`/acknowledgements/${token}/conflict`, data);
    return response.data;
  },

  /**
   * Get conflicted acknowledgements for admin review
   */
  getConflicted: async (page = 1, limit = 25, search = '') => {
    const response = await apiClient.get('/acknowledgements/conflicted', {
      params: { page, limit, search },
    });
    return response.data;
  },

  /**
   * Admin resolves conflicted acknowledgement
   * @param {number} id - Acknowledgement ID
   * @param {Object} data - Optional data with final_values for admin's selected values
   */
  resolve: async (id, data = {}) => {
    const response = await apiClient.post(`/acknowledgements/${id}/resolve`, data);
    return response.data;
  },

  /**
   * Admin requests vendor to correct questionnaire (for conflicted acknowledgements)
   * Resets acknowledgement and sends email with questionnaire link + remarks
   * @param {number} acknowledgementId - Acknowledgement ID
   * @param {Object} data - { additional_remarks: string, updated_values?: object }
   */
  requestCorrection: async (acknowledgementId, { additional_remarks, updated_values }) => {
    const body = { additional_remarks };
    if (updated_values && Object.keys(updated_values).length > 0) {
      body.updated_values = updated_values;
    }
    const response = await apiClient.post(
      `/acknowledgements/${acknowledgementId}/request-correction`,
      body
    );
    return response.data;
  },

  /**
   * Resend acknowledgement email
   */
  resend: async (id) => {
    const response = await apiClient.post(`/acknowledgements/${id}/resend`);
    return response.data;
  },
};

// =============================================================================
// LINK TOKEN SERVICE
// =============================================================================
// Cross-stage vendor link registry (/api/v1/link-tokens). Surfaces registration,
// rectification, acknowledgement and regularization links in one place, with
// extend/revoke/regenerate actions and per-vendor journey.

export const linkTokenService = {
  /**
   * List link tokens with filtering + server-side search + pagination.
   * Array params (stage, status) are passed through as arrays; axios serializes
   * them as repeated query keys which the backend parses as multi-select.
   */
  getLinkTokens: async (params = {}) => {
    const response = await apiClient.get('/link-tokens', { params });
    return response.data;
  },

  /**
   * Per-stage counts (+ total) for the given filters (stage ignored). Powers the
   * stage tab badges. Returns { counts: { REGISTRATION: n, ... }, total }.
   */
  getStageCounts: async (params = {}) => {
    const response = await apiClient.get('/link-tokens/stage-counts', { params });
    return response.data;
  },

  /**
   * Full onboarding chain for one vendor (supersede chains resolved).
   */
  getJourney: async (vendorId) => {
    const response = await apiClient.get(`/link-tokens/journey/${vendorId}`);
    return response.data;
  },

  /**
   * Append-only event trail for one token.
   */
  getEvents: async (id) => {
    const response = await apiClient.get(`/link-tokens/${id}/events`);
    return response.data;
  },

  /**
   * Current live URL for a token (server reads the raw token from the source
   * table). Returns { live, link }.
   */
  getLink: async (id) => {
    const response = await apiClient.get(`/link-tokens/${id}/link`);
    return response.data;
  },

  /**
   * Extend expiry. body: { days, reason }
   */
  extend: async (id, body) => {
    const response = await apiClient.post(`/link-tokens/${id}/extend`, body);
    return response.data;
  },

  /**
   * Revoke. body: { reason }
   */
  revoke: async (id, body) => {
    const response = await apiClient.post(`/link-tokens/${id}/revoke`, body);
    return response.data;
  },

  /**
   * Regenerate. body: { expires_in_days? }
   */
  regenerate: async (id, body = {}) => {
    const response = await apiClient.post(`/link-tokens/${id}/regenerate`, body);
    return response.data;
  },

  /**
   * Nudge the vendor about a still-open link. Shares the hourly worker's 24h
   * per-recipient rate limit, so this can legitimately come back as a 422.
   */
  remind: async (id) => {
    const response = await apiClient.post(`/link-tokens/${id}/remind`);
    return response.data;
  },
};

// =============================================================================
// INVITE SERVICE
// =============================================================================

/**
 * Approval routing: who approves which invites, and who clears the quality gate
 * (User Management › Invite approvers / Quality approvers).
 */
export const approvalRoutingService = {
  getOptions: async () => {
    const response = await apiClient.get('/approval-routing/options');
    return response.data;
  },
  getRules: async (kind) => {
    const response = await apiClient.get('/approval-routing/rules', { params: kind ? { kind } : {} });
    return response.data;
  },
  getApprovers: async () => {
    const response = await apiClient.get('/approval-routing/approvers');
    return response.data;
  },
  createRule: async (rule) => {
    const response = await apiClient.post('/approval-routing/rules', rule);
    return response.data;
  },
  deleteRule: async (id) => {
    const response = await apiClient.delete(`/approval-routing/rules/${id}`);
    return response.data;
  },
  /** Everything routed to (or decided by) one approver: { user_id } or { email }. */
  getApproverActivity: async (who) => {
    const response = await apiClient.get('/approval-routing/approver-activity', { params: who });
    return response.data;
  },
};

export const inviteService = {
  /**
   * Get all invites with filtering
   */
  getInvites: async (params = {}) => {
    const response = await apiClient.get('/invites', { params });
    return response.data;
  },

  /**
   * Get invite by ID
   */
  getInviteById: async (id) => {
    const response = await apiClient.get(`/invites/${id}`);
    return response.data;
  },

  /**
   * Validate invite token (public endpoint)
   */
  validateToken: async (token) => {
    const response = await publicApiClient.get(`/invites/validate/${token}`);
    return response.data;
  },

  /**
   * Create new invite (name, email + nature_of_business, oem_odm, supplier_subcategory).
   * The invite is routed for approval; the supplier gets the link once it is approved.
   */
  createInvite: async (data) => {
    const response = await apiClient.post('/invites', data);
    return response.data;
  },

  /**
   * Invites waiting for approval (admins see all, approvers see their own)
   */
  getApprovals: async (params = {}) => {
    const response = await apiClient.get('/invites/approvals', { params });
    return response.data;
  },

  approveInvite: async (id, comments) => {
    const response = await apiClient.post(`/invites/${id}/approve`, comments ? { comments } : {});
    return response.data;
  },

  rejectInvite: async (id, comments) => {
    const response = await apiClient.post(`/invites/${id}/reject`, { comments });
    return response.data;
  },

  /**
   * Delete invite
   */
  deleteInvite: async (id) => {
    const response = await apiClient.delete(`/invites/${id}`);
    return response.data;
  },

  /**
   * Resend invite
   */
  resendInvite: async (id) => {
    const response = await apiClient.post(`/invites/${id}/resend`);
    return response.data;
  },
};

// =============================================================================
// APPROVAL SERVICE
// =============================================================================

export const approvalService = {
  /**
   * Get all approvals with filtering
   */
  getApprovals: async (params = {}) => {
    const response = await apiClient.get('/approvals', { params });
    return response.data;
  },

  /**
   * Get pending approvals
   */
  getPendingApprovals: async (level) => {
    const response = await apiClient.get('/approvals/pending', { params: { level } });
    return response.data;
  },

  /**
   * Get approval statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/approvals/stats');
    return response.data;
  },

  /**
   * Get approval history for vendor
   */
  getVendorHistory: async (vendorId) => {
    const response = await apiClient.get(`/approvals/vendor/${vendorId}/history`);
    return response.data;
  },

  /**
   * Submit approval decision
   */
  submitDecision: async (vendorId, level, status, comments, questionnaire) => {
    const response = await apiClient.post('/approvals', {
      vendor_id: vendorId,
      level,
      status,
      comments,
      questionnaire,
    });
    return response.data;
  },
};

// =============================================================================
// VENDOR DOCUMENTS SERVICE (Public - Token-based)
// =============================================================================

export const vendorDocumentsService = {
  /**
   * Get vendor info and document list by token (public)
   */
  getByToken: async (token) => {
    const response = await publicApiClient.get(`/vendor-documents/${token}`);
    return response.data;
  },

  /**
   * Preview URL - open in new tab
   */
  previewUrl: (token, docId) =>
    `${API_BASE_URL}/vendor-documents/${token}/documents/${docId}/preview`,

  /**
   * Download document - fetch and trigger download
   */
  download: async (token, docId, filename) => {
    const response = await publicApiClient.get(
      `/vendor-documents/${token}/documents/${docId}/download`,
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};

// =============================================================================
// APPROVAL DECISION LINKS (Public) — approvers without a VMS account
// =============================================================================

export const approvalLinkService = {
  /** The one invite or supplier this link was sent for, or who already decided it. */
  view: async (token) => {
    const response = await publicApiClient.get(`/approval-links/${token}`);
    return response.data;
  },
  decide: async (token, decision, comments) => {
    const response = await publicApiClient.post(`/approval-links/${token}/decide`, { decision, comments: comments || null });
    return response.data;
  },
  /** An expired link: a new one is e-mailed to the same address. */
  renew: async (token) => {
    const response = await publicApiClient.post(`/approval-links/${token}/renew`);
    return response.data;
  },
  previewUrl: (token, docId) => `${API_BASE_URL}/approval-links/${token}/documents/${docId}/preview`,
  /** The document itself, for showing it inside the page (a blob URL is never blocked from framing). */
  fetchDocument: async (token, docId) => {
    const response = await publicApiClient.get(`/approval-links/${token}/documents/${docId}/preview`, { responseType: 'blob' });
    return response.data;
  },
  download: async (token, docId, filename) => {
    const response = await publicApiClient.get(`/approval-links/${token}/documents/${docId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};

// =============================================================================
// DOCUMENT REQUEST SERVICE (Public)
// =============================================================================

export const documentRequestService = {
  /**
   * Get document request by token (public)
   */
  getByToken: async (token) => {
    const response = await publicApiClient.get(`/document-requests/${token}`);
    return response.data;
  },

  /**
   * Submit documents (public, multipart/form-data)
   */
  submit: async (token, formData) => {
    const publicUploadClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: UPLOAD_TIMEOUT,
      withCredentials: false,
      headers: {
        'Accept': 'application/json',
      },
    });
    const response = await publicUploadClient.post(`/document-requests/${token}/submit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// =============================================================================
// REGULARIZATION SERVICE
// =============================================================================

export const regularizationService = {
  /**
   * Get regularization statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/regularization/stats');
    return response.data;
  },

  /**
   * List batches with pagination and filtering
   */
  getBatches: async (page = 1, limit = 25, status, search) => {
    const params = { page, limit };
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await apiClient.get('/regularization/batches', { params });
    return response.data;
  },

  /**
   * Get batch details
   */
  getBatchDetails: async (batchId) => {
    const response = await apiClient.get(`/regularization/batch/${batchId}`);
    return response.data;
  },

  /**
   * Get eligible vendors for regularization
   */
  getEligibleVendors: async (search) => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/regularization/eligible-vendors', { params });
    return response.data;
  },

  /**
   * Send bulk regularization links
   */
  sendBulk: async (vendorIds) => {
    const response = await apiClient.post('/regularization/send-bulk', {
      vendor_ids: vendorIds.map(id => Number(id)), // Ensure numbers are sent
    });
    return response.data;
  },

  /**
   * Get request by token (public)
   */
  getRequestByToken: async (token) => {
    const response = await apiClient.get(`/regularization/request/${token}`);
    return response.data;
  },

  /**
   * Preview existing vendor document by token + type (public, for "Keep existing" in regularization flow)
   */
  previewDocument: async (token, documentType) => {
    const response = await apiClient.get(
      `/regularization/request/${token}/documents/${documentType}/preview`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Submit vendor updates (public)
   */
  submitUpdates: async (token, data) => {
    const response = await apiClient.post(`/regularization/submit/${token}`, data);
    return response.data;
  },

  /**
   * Verify request (admin)
   */
  verifyRequest: async (requestId) => {
    const response = await apiClient.post(`/regularization/verify/${requestId}`);
    return response.data;
  },
};

// =============================================================================
// CAMPAIGNS (Campaigning Tool / CampaignOS read model — admin only)
// Backend: vendorhub-backend/src/modules/campaigns (EmailCampaign / EmailMessage /
// EmailEvent written by the bulk-send pipeline + real Vendor audience).
// =============================================================================
export const campaignService = {
  /** GET /campaigns?page&limit&status&type&search → { data: CampaignRow[], meta } */
  list: async (params = {}) => (await apiClient.get('/campaigns', { params })).data,

  /** GET /campaigns/summary → Dashboard KPIs, funnel, monthly activity, regularization stats */
  summary: async () => (await apiClient.get('/campaigns/summary')).data,

  /** GET /campaigns/analytics → engagement trend, open latency histogram, top campaigns, geo */
  analytics: async () => (await apiClient.get('/campaigns/analytics')).data,

  /** GET /campaigns/audience?status&region&country&currency&msme&search&page&limit */
  audience: async (params = {}) => (await apiClient.get('/campaigns/audience', { params })).data,

  /** GET /campaigns/audience/ids?…filters → { ids: number[], matched } — every vendor the filters match */
  audienceIds: async (params = {}) => (await apiClient.get('/campaigns/audience/ids', { params })).data,

  /**
   * GET /campaigns/audience/recipients?…filters → { recipients, matched, capped }
   * The matching vendors WITH addresses — what the wizard fills its CC list from.
   * Capped at the provider's per-field ceiling; 'capped' says when the filter matched more.
   */
  audienceRecipients: async (params = {}) =>
    (await apiClient.get('/campaigns/audience/recipients', { params })).data,

  /** GET /campaigns/:id → row + status_breakdown + daily (30d) + recent_events */
  get: async (id) => (await apiClient.get(`/campaigns/${id}`)).data,

  /** GET /campaigns/:id/messages?status&kind&page&limit — status may be an EmailMessageStatus or "failed"; kind is TO/CC/BCC */
  messages: async (id, params = {}) => (await apiClient.get(`/campaigns/${id}/messages`, { params })).data,

  /**
   * GET /campaigns/:id/not-sent.xlsx — the vendors who did not get the mail (code, name,
   * email, reason) as a sheet. Fetches it with the session and hands it to the browser as a
   * download; resolves with the filename used.
   */
  downloadNotSent: async (id) => {
    const response = await apiClient.get(`/campaigns/${id}/not-sent.xlsx`, { responseType: 'blob' });
    const filename = attachmentFilename(response.headers?.['content-disposition'], `CMP-${id}-not-sent.xlsx`);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return filename;
  },

  /** GET /campaigns/:id/failure-analysis → why each mail did not arrive, and who can still be reached (Advanced Analytics). */
  failureAnalysis: async (id) => (await apiClient.get(`/campaigns/${id}/failure-analysis`, { timeout: 120000 })).data,

  /**
   * GET /campaigns/:id/failure-analysis.xlsx → the same as a workbook; triggers the browser download.
   * With `reason`, only that reason's addresses (e.g. 'placeholder' → every fake-placeholder bounce).
   */
  downloadFailureAnalysis: async (id, reason) => {
    const response = await apiClient.get(`/campaigns/${id}/failure-analysis.xlsx`, {
      params: reason ? { reason } : undefined,
      responseType: 'blob',
      timeout: 120000,
    });
    // Named by campaign and reason even when the header cannot be read across origins, so
    // "fake placeholder" and "mailbox missing" never save as the same file.
    const filename = attachmentFilename(response.headers?.['content-disposition'], failureExcelName(id, reason));
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return filename;
  },

  /** GET /campaigns/email-fixes → vendors whose email field is not one clean address, with the proposed correction. */
  emailFixes: async () => (await apiClient.get('/campaigns/email-fixes', { timeout: 120000 })).data,

  /** POST /campaigns/email-fixes/apply { fixes: [{ vendor_id, email }] } → { updated, unchanged, rejected } (audited). */
  applyEmailFixes: async (fixes) => (await apiClient.post('/campaigns/email-fixes/apply', { fixes })).data,

  /** POST /campaigns/:id/retry-failed → { requeued, runId } (409 MAIL_TRANSPORT_UNCONFIGURED when no transport is configured) */
  retryFailed: async (id) => (await apiClient.post(`/campaigns/${id}/retry-failed`)).data,
  /**
   * POST /campaigns/:id/resend → resend to the vendors the campaign did not reach.
   * dryRun (default) returns the numbers and writes nothing; false creates and queues it.
   * `vendorIds` limits it to one failure reason's vendors; `label` names that reason in the new campaign.
   */
  resend: async (id, dryRun = true, { vendorIds, label } = {}) =>
    (
      await apiClient.post(
        `/campaigns/${id}/resend`,
        { dry_run: dryRun, ...(vendorIds?.length ? { vendor_ids: vendorIds } : {}), ...(label ? { label } : {}) },
        { timeout: 120000 }
      )
    ).data,

  /** POST /campaigns/broadcast/preview → { subject, html, recipient_count, sample_recipients } (writes nothing) */
  previewBroadcast: async (body) => (await apiClient.post('/campaigns/broadcast/preview', body)).data,

  /**
   * POST /campaigns/broadcast/precheck → { checked, reachable, recipients, copies, problems[] }
   * Same body as the launch; sends and writes nothing. `problems[].reason` is
   * 'unsendable' | 'bounced' | 'soft_bounced'.
   */
  // The precheck and the launch both build the recipient plan, which can take ~20 s on a cold DNS
  // cache: past the default timeout a launch would look failed while the campaign is in fact
  // queued — and invite a second click.
  precheckBroadcast: async (body) => (await apiClient.post('/campaigns/broadcast/precheck', body, { timeout: 120000 })).data,

  /** POST /campaigns/broadcast → { campaign_id, queued, skipped, run_id, subject } (409 MAIL_TRANSPORT_UNCONFIGURED when no transport is configured) */
  sendBroadcast: async (body) => (await apiClient.post('/campaigns/broadcast', body, { timeout: 120000 })).data,
  /** POST /campaigns/audience/import — upload the lot Excel/CSV; codes resolve against the live Vendor table */
  importAudience: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await apiClient.post('/campaigns/audience/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },

  /** GET /campaigns/sync-status → { provider, configured, webhook_configured, supports_event_pull } */
  syncStatus: async () => (await apiClient.get('/campaigns/sync-status')).data,
};

// =============================================================================
// SUPPLIER REQUEST SERVICE
// =============================================================================

export const supplierRequestService = {
  /**
   * Get all supplier requests
   */
  getSupplierRequests: async (params = {}) => {
    const response = await apiClient.get('/supplier-requests', { params });
    return response.data;
  },

  /**
   * Get request by ID
   */
  getRequestById: async (id) => {
    const response = await apiClient.get(`/supplier-requests/${id}`);
    return response.data;
  },

  /**
   * Create draft request
   */
  createDraft: async (data) => {
    const response = await apiClient.post('/supplier-requests', { ...data, status: 'DRAFT' });
    return response.data;
  },

  /**
   * Update draft request
   */
  updateDraft: async (id, data) => {
    const response = await apiClient.put(`/supplier-requests/${id}`, data);
    return response.data;
  },

  /**
   * Submit request
   */
  submitRequest: async (id) => {
    const response = await apiClient.post(`/supplier-requests/${id}/submit`);
    return response.data;
  },

  /**
   * Approve request
   */
  approveRequest: async (id, classification = {}) => {
    const response = await apiClient.post(`/supplier-requests/${id}/approve`, classification);
    return response.data;
  },

  /**
   * Reject request
   */
  rejectRequest: async (id, reason) => {
    const response = await apiClient.post(`/supplier-requests/${id}/reject`, { reason });
    return response.data;
  },

  /**
   * Delete request
   */
  deleteRequest: async (id) => {
    const response = await apiClient.delete(`/supplier-requests/${id}`);
    return response.data;
  },
};

// =============================================================================
// DASHBOARD SERVICE
// =============================================================================

export const dashboardService = {
  /**
   * Get dashboard metrics
   */
  getMetrics: async () => {
    const response = await apiClient.get('/dashboard/metrics');
    const data = response.data.data;
    
    // Transform backend response to frontend expected format
    return {
      data: {
        totalVendors: {
          value: data.vendors?.total || 0,
          change: '',
          changeType: 'neutral',
        },
        pendingApprovals: {
          value: data.approvals?.total_pending || 0,
          change: '',
          changeType: 'neutral',
        },
        rejectedVendors: {
          value: data.vendors?.by_status?.REJECTED || 0,
          change: '',
          changeType: 'neutral',
        },
        thisMonthOnboarded: {
          value: data.vendors?.this_month_onboarded || 0,
          change: data.vendors?.this_month_change_pct != null
            ? `${data.vendors.this_month_change_pct > 0 ? '+' : ''}${data.vendors.this_month_change_pct}%`
            : '',
          changeType: data.vendors?.this_month_change_pct > 0
            ? 'positive'
            : data.vendors?.this_month_change_pct < 0 ? 'negative' : 'neutral',
        },
        vendorDistribution: data.vendors?.by_status || {},
        countrySplit: data.vendors?.by_country || [],
        msmeSplit: data.vendors?.by_msme || {},
        recentVendors: data.recent_vendors || [],
      },
    };
  },

  /**
   * Get onboarding trends
   */
  getOnboardingTrends: async () => {
    const response = await apiClient.get('/dashboard/onboarding-trends');
    return response.data;
  },

  /**
   * Get activity feed
   */
  getActivityFeed: async (limit = 20) => {
    const response = await apiClient.get('/dashboard/activity-feed', { params: { limit } });
    return response.data;
  },

  /**
   * Get workflow statistics
   */
  getWorkflowStats: async () => {
    const response = await apiClient.get('/dashboard/workflow-stats');
    return response.data;
  },
};

// =============================================================================
// MIS REPORT SERVICE (read-only management report; terminal-state population)
// =============================================================================
export const misReportService = {
  getSummary: async (params) => (await apiClient.get('/reports/mis/summary', { params })).data.data,
  getTrend: async (params) => (await apiClient.get('/reports/mis/trend', { params })).data.data,
  getDistribution: async (dimension, params) =>
    (await apiClient.get('/reports/mis/distribution', { params: { dimension, ...params } })).data.data,
  getPipeline: async (params) => (await apiClient.get('/reports/mis/pipeline', { params })).data.data,
  getInFlight: async () => (await apiClient.get('/reports/mis/pipeline/in-flight')).data.data,
  getRecords: async (params) => (await apiClient.get('/reports/mis/records', { params })).data.data,
  exportExcel: async (params) => {
    const response = await apiClient.get('/reports/mis/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// =============================================================================
// VENDOR CODE CONFLICT SERVICE (ERP sync manual resolution)
// =============================================================================

export const vendorCodeConflictService = {
  /**
   * Get pending ERP code conflicts
   */
  getConflicts: async (page = 1, limit = 25, search = '') => {
    const response = await apiClient.get('/vendors/code-conflicts', {
      params: { page, limit, ...(search ? { search } : {}) },
    });
    return response.data;
  },

  /**
   * Lightweight aggregate for the sidebar badge — companies that need attention on the
   * Code Conflicts screen (pending conflicts + duplicate-vendor groups). COUNT only; never
   * pulls conflict rows. Returns { conflicts, duplicates, total }.
   */
  getCount: async () => {
    const response = await apiClient.get('/vendors/code-conflicts/count');
    return response.data;
  },

  /**
   * Code-less vendor rows whose company already has a coded row. These can never get a
   * code (codes are globally unique) and need merging or removal.
   */
  getDuplicateVendors: async (page = 1, limit = 25, search = '') => {
    const response = await apiClient.get('/vendors/duplicate-vendors', {
      params: { page, limit, ...(search ? { search } : {}) },
    });
    return response.data;
  },

  /**
   * Live ERP codes for one vendor's company name, each flagged as free or already held.
   * A company registered in several states owns one ERP code per GST registration, so a
   * duplicate row can often take a code of its own.
   */
  getErpCandidates: async (vendorId) => {
    const response = await apiClient.get(`/vendors/${vendorId}/erp-candidates`);
    return response.data;
  },

  /** Assign one or more ERP codes to a vendor (adds to any it already holds). */
  assignErpCodes: async (vendorId, codes) => {
    const response = await apiClient.post(`/vendors/${vendorId}/assign-erp-codes`, { codes });
    return response.data;
  },

  /**
   * Resolve a conflict by choosing one or more ERP candidate codes
   */
  resolve: async (id, codes) => {
    const response = await apiClient.post(`/vendors/code-conflicts/${id}/resolve`, { codes });
    return response.data;
  },

  /**
   * Dismiss a conflict (none of the candidates is this vendor)
   */
  dismiss: async (conflictId) => {
    const response = await apiClient.post(`/vendors/code-conflicts/${conflictId}/dismiss`);
    return response.data;
  },
};

// =============================================================================
// HEALTH SERVICE
// =============================================================================

export const healthService = {
  /**
   * Check API health
   */
  check: async () => {
    const response = await apiClient.get('/health', { 
      baseURL: API_BASE_URL.replace('/api/v1', ''),
      timeout: 5000,
    });
    return response.data;
  },
};

// Export default client for custom requests
export default apiClient;

/**
 * Reusable email templates (GET/POST/PATCH/DELETE /email-templates). A template is
 * CONTENT ONLY — subject + body fields; campaigns render it at send time.
 */
export const templateService = {
  list: async (params = {}) => (await apiClient.get('/email-templates', { params })).data,
  get: async (id) => (await apiClient.get(`/email-templates/${id}`)).data,
  create: async (body) => (await apiClient.post('/email-templates', body)).data,
  update: async (id, body) => (await apiClient.patch(`/email-templates/${id}`, body)).data,
  duplicate: async (id) => (await apiClient.post(`/email-templates/${id}/duplicate`, {})).data,
  remove: async (id) => (await apiClient.delete(`/email-templates/${id}`)).data,
  /** The mail a (half-written) template renders to, merge tags filled as a sample vendor. Writes nothing. */
  preview: async (body) => (await apiClient.post('/email-templates/preview', body)).data,
  /** Mail the rendered template to the logged-in operator's own address (the server picks the address). */
  testSend: async (body) => (await apiClient.post('/email-templates/test-send', body, { timeout: 60_000 })).data,
  /** An image for the scratch pad: stored on the server, answered with its public URL. */
  uploadImage: async (file) => {
    const form = new FormData();
    form.append('image', file, file.name);
    return (await apiClient.post('/email-templates/assets', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 })).data;
  },
};

/**
 * Regularization through the vendor: the admin picks who gets the link and what it asks for; the
 * vendor fills the form. Backend: vendorhub-backend/src/modules/regularization.
 */
export const throughVendorService = {
  /** GET /regularization/links — every send, what it asked for, where each vendor's link stands */
  listBatches: async (params = {}) => (await apiClient.get('/regularization/links', { params })).data,
  /** GET /regularization/links/:id */
  getBatch: async (id) => (await apiClient.get(`/regularization/links/${id}`)).data,
  /** GET /regularization/links/latest — the newest link per vendor, for colouring rows */
  latestLinks: async () => (await apiClient.get('/regularization/links/latest')).data,
  /** POST /regularization/send-bulk { vendor_ids, asks, segment } — one email with a personal link each */
  sendLinks: async ({ vendor_ids, asks, segment }) => (await apiClient.post('/regularization/send-bulk', { vendor_ids, asks, segment })).data,
  /** POST /regularization/remind { vendor_ids } — the same link, one more mail, to unanswered links only */
  remind: async ({ vendor_ids }) => (await apiClient.post('/regularization/remind', { vendor_ids })).data,
  /** POST /regularization/links/:batchId/resend { vendor_ids } — the same link again, to open links whose mail failed or bounced → { resent, failed, skipped } */
  resend: async ({ batchId, vendor_ids }) => (await apiClient.post(`/regularization/links/${batchId}/resend`, { vendor_ids })).data,
};

/**
 * Self-regularization — verify what is on a vendor's record against the registries, with no
 * email to the vendor, then approve the result into the record. Backend:
 * vendorhub-backend/src/modules/self-regularization (contract in
 * docs/superpowers/specs/2026-09-12-self-regularization-design.md §5).
 */
export const selfRegularizationService = {
  /** GET /self-regularization/checks → { data: checks with real prices/availability, meta: { provider, prices_configured, provider_problem } } */
  listChecks: async () => (await apiClient.get('/self-regularization/checks')).data,
  /** GET /self-regularization/vendor-state → per vendor: inputs on record, malformed inputs, verified checks */
  vendorState: async () => (await apiClient.get('/self-regularization/vendor-state')).data,
  /** POST /self-regularization/estimate { checks, vendor_ids } */
  estimate: async ({ checks, vendor_ids }) => (await apiClient.post('/self-regularization/estimate', { checks, vendor_ids })).data,
  /** GET /self-regularization/runs */
  listRuns: async (params = {}) => (await apiClient.get('/self-regularization/runs', { params })).data,
  /** GET /self-regularization/runs/:id */
  getRun: async (id) => (await apiClient.get(`/self-regularization/runs/${id}`)).data,
  /** GET /self-regularization/spend → what verification has cost: totals, by check, by day/month, recent runs (mock runs free) */
  spend: async () => (await apiClient.get('/self-regularization/spend')).data,
  /** POST /self-regularization/runs { name, checks, vendor_ids } — starts verifying at once */
  createRun: async ({ name, checks, vendor_ids }) => (await apiClient.post('/self-regularization/runs', { name, checks, vendor_ids })).data,
  /** POST /self-regularization/runs/:id/approve { vendor_ids?, exclude? } — writes to vendor records */
  approveRun: async (id, body = {}) => (await apiClient.post(`/self-regularization/runs/${id}/approve`, body)).data,
  /** POST /self-regularization/runs/:id/request-acknowledgement — one mail to each approved vendor, after approval only */
  requestAcknowledgement: async (id) => (await apiClient.post(`/self-regularization/runs/${id}/request-acknowledgement`, {})).data,
  /**
   * GET /self-regularization/runs/:id/export?bucket=all|attention|clean|other — the tab on screen
   * as a workbook (Summary, Vendors, Facts). Fetches it with the session and hands it to the
   * browser as a download; resolves with the filename used.
   */
  exportRun: async (id, bucket = 'all') => {
    const response = await apiClient.get(`/self-regularization/runs/${id}/export`, { params: { bucket }, responseType: 'blob' });
    const match = /filename="([^"]+)"/.exec(response.headers?.['content-disposition'] || '');
    const filename = match ? match[1] : `verification-run-${id}-${bucket}.xlsx`;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return filename;
  },
};

/** The vendor's side of an approved run: the public page behind the acknowledgement email. */
export const verificationAcknowledgementService = {
  /** GET /self-regularization/acknowledgement/:token (public) */
  get: async (token) => (await apiClient.get(`/self-regularization/acknowledgement/${encodeURIComponent(token)}`)).data,
  /** POST /self-regularization/acknowledgement/:token/respond { answer: 'confirm' | 'dispute', name?, note? } (public) */
  respond: async (token, body) => (await apiClient.post(`/self-regularization/acknowledgement/${encodeURIComponent(token)}/respond`, body)).data,
};

/** Read-only provider consumption: call counts (GET /surepass-usage/summary) and what they cost (GET /surepass-usage/spend). */
export const surepassUsageService = {
  summary: async (days = 180) => (await apiClient.get('/surepass-usage/summary', { params: { days } })).data,
  /** GET /surepass-usage/spend → every rupee from every source: totals with GST, by API group, by source, by day/month, recent calls, the credit balance */
  spend: async () => (await apiClient.get('/surepass-usage/spend')).data,
  /** GET /surepass-usage/credits → { balance, entries (newest first), gst_percent, low_balance_inr } */
  credits: async () => (await apiClient.get('/surepass-usage/credits')).data,
  /** POST /surepass-usage/credits { kind: opening_balance|top_up|adjustment, amount_inr, effective_at?, note? } — admin only */
  addCredit: async (payload) => (await apiClient.post('/surepass-usage/credits', payload)).data,
  /** DELETE /surepass-usage/credits/:id — admin only; the entry is hidden, never deleted */
  removeCredit: async (id) => (await apiClient.delete(`/surepass-usage/credits/${id}`)).data,
};
